import { useQuery } from '@tanstack/react-query';

import { mediaManagerClient } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { ProgramBroadcastFiltersState } from '@/stores/program-broadcast-filters';
import { ParamsWithPagination } from '@/types/api';

import { SegmentFilters, TSegment } from '../types';

type TSegmentResponse = {
  data: TSegment[];
  total: number;
};

export async function getSegments(
  params?: ParamsWithPagination<SegmentFilters>,
  filters?: Partial<ProgramBroadcastFiltersState>
): Promise<TSegmentResponse> {
  return mediaManagerClient
    .get<TSegmentResponse>('/api/v1/segments', {
      params: {
        ...(params?.limit && { limit: params.limit }),
        ...(params?.offset && { offset: params.offset }),
        ...(params?.search && { search: params.search }),
        ...(params?.idIn && { id__in: params.idIn.join(',') }),
        ...(params?.channelIdIn && { channel_id__in: params.channelIdIn.join(',') }),
        ...(params?.startDatetimeGte && { start_datetime__gte: params.startDatetimeGte }),
        ...(params?.endDatetimeLte && { end_datetime__lte: params.endDatetimeLte }),
        ...(params?.createdAtLte && { created_at__lte: params.createdAtLte }),
        ...(filters?.search && { search: filters.search }),
        ...(filters?.gte && { start_datetime__gte: filters.gte }),
        ...(filters?.lte && { end_datetime__lte: filters.lte }),
        ...(filters?.channelType && { media_type__in: filters.channelType }),
        ...(filters?.review_states &&
          filters.review_states.length > 0 && {
            review_states: filters.review_states.join(','),
          }),
        ...(filters?.logos &&
          filters?.logos.length > 0 && { logo_id__in: filters.logos.join(',') }),
        ...(filters?.broadcasters &&
          filters?.broadcasters.length > 0 && {
            channel_id__in: filters.broadcasters.join(','),
          }),
        ...(filters?.programs &&
          filters?.programs.length > 0 && {
            program_id__in: filters.programs.join(','),
          }),
        ...(filters?.people &&
          filters?.people.length > 0 && {
            people_id__in: filters.people.join(','),
          }),
        ...(filters?.workspaces &&
          filters?.workspaces.length > 0 && {
            workspace_id__in: filters.workspaces.join(','),
          }),
        ...(filters?.projects &&
          filters?.projects.length > 0 && {
            project_id__in: filters.projects.join(','),
          }),
        ...(filters?.clients &&
          filters?.clients.length > 0 && {
            client_id__in: filters.clients.join(','),
          }),
        ...(filters?.audienceChannelSource && {
          audience_channel_source: filters.audienceChannelSource,
        }),
        ...(filters?.tags &&
          filters?.tags.length > 0 && {
            tag_id__in: filters.tags.join(','),
          }),
        ...(filters?.startDatetimeLte &&
          !filters.gte && { start_datetime__lte: filters.startDatetimeLte }),
        ...(filters?.startDatetimeGte &&
          !filters.gte && { start_datetime__gte: filters.startDatetimeGte }),
        ...(filters?.endDatetimeGte &&
          !filters.lte && { end_datetime__gte: filters.endDatetimeGte }),
        ...(filters?.endDatetimeLte &&
          !filters.lte && { end_datetime__lte: filters.endDatetimeLte }),
        ...(filters?.order_by && { order_by: filters.order_by }),
      },
    })
    .then(response => response as unknown as TSegmentResponse);
}

type QueryFnType = typeof getSegments;

interface UseSegmentsParams {
  config?: QueryConfig<QueryFnType>;
  params?: ParamsWithPagination<SegmentFilters>;
  filters?: Partial<ProgramBroadcastFiltersState>;
}

export function useSegments({ config, params, filters }: UseSegmentsParams = {}) {
  return useQuery({
    ...config,
    queryKey: ['segments', params, filters],
    queryFn: () => getSegments(params, filters),
    refetchInterval: 2 * 60 * 1000, // Refresca cada 2 minutos
  });
}
